import React, { useEffect } from "react"
import { MdOutlineClose } from "react-icons/md"
import styles from "../quotation/markQuotationStatusConfirmPopup.module.scss"

type Props = {
    isOpen: boolean
    quotationName?: string | null
    staCode?: string | null
    isSubmitting?: boolean
    onCancel: () => void
    onConfirm: () => void
}

const buildDeleteTargetLabel = (
    quotationName?: string | null,
    staCode?: string | null,
): string => {
    const name = String(quotationName || "").trim()
    const code = String(staCode || "").trim()

    if (name && code) return `${name} (${code})`
    if (name) return name
    if (code) return code
    return "this quotation"
}

const QuotationDeleteConfirmPopup: React.FC<Props> = ({
    isOpen,
    quotationName,
    staCode,
    isSubmitting = false,
    onCancel,
    onConfirm,
}) => {
    useEffect(() => {
        if (!isOpen) return

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !isSubmitting) {
                onCancel()
            }
        }

        document.addEventListener("keydown", handleEscape)
        document.body.style.overflow = "hidden"

        return () => {
            document.removeEventListener("keydown", handleEscape)
            document.body.style.overflow = "unset"
        }
    }, [isOpen, isSubmitting, onCancel])

    if (!isOpen) return null

    const deleteTargetLabel = buildDeleteTargetLabel(quotationName, staCode)

    const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && !isSubmitting) {
            onCancel()
        }
    }

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-quotation-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className={[styles.iconCircle, styles.iconCircleLost].join(" ")}>
                    <MdOutlineClose size={28} color="#991B1B" />
                </div>

                <h2 id="delete-quotation-title" className={styles.title}>
                    Delete Quotation
                </h2>

                <p className={styles.message}>
                    Are you sure you want to delete <strong>{deleteTargetLabel}</strong>?
                </p>

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={[styles.confirmButton, styles.confirmButtonDanger].join(" ")}
                        onClick={onConfirm}
                        disabled={isSubmitting}
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    )
}

export default QuotationDeleteConfirmPopup
